<header class="go-page-title">
  <h1 class="go-heading-1">Off Canvas</h1>
  <a
  [href]="linkToSource"
  target="_blank">
    <go-button buttonVariant="neutral"> View on GitHub </go-button>
  </a>
</header>

<div class="go-container">

  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          The <code class="code-block--inline">&lt;go-off-canvas&gt;</code>
          component should be the only instance of the off canvas in the app.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h3 class="go-heading-3">Setup</h3>
        <p class="go-body-copy">
          To use the off canvas anywhere in the app, you must first add
          it to the app level. This should only be done once per application.
        </p>
        <ol class="go-list go-list--ordered">
          <li>
            <p class="go-body-copy">
              Import the <code class="code-block--inline">GoOffCanvasModule</code>
              into the main app module.
            </p>
            <code [highlight]="appModuleImport"></code>
          </li>
          <li>
            <p class="go-body-copy">
              Add the <code class="code-block--inline">&lt;go-off-canvas&gt;</code>
              element to <code class="code-block--inline">app.component.html</code>.
            </p>
          </li>
        </ol>
      </div>

    </div>
  </go-card>

  <go-toast
    class="go-column go-column--100"
    header="Note!"
    [message]="noteMessage">
  </go-toast>

  <go-card class="go-column go-column--100" id="service">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Using the Off Canvas service</h2>
      <go-copy cardId="service" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100 go-column--no-padding">
        <p class="go-body-copy">To make use of the off canvas in our component:</p>
        <ol class="go-list go-list--ordered">
          <li>
            <p class="go-body-copy">
              In the module that imports your component, import the
              <code class="code-block--inline">GoOffCanvasModule</code>
              and add the <code class="code-block--inline">GoOffCanvasService</code>
              to the providers.
            </p>
            <code [highlight]="uiKitExample"></code>
          </li>
          <li>
            <p class="go-body-copy">
              In the component that is telling the off canvas to open, import the
              <code class="code-block--inline">GoOffCanvasService</code>, and the
              component that is being rendered inside the off canvas.
            </p>
            <code [highlight]="implementationExample"></code>
          </li>
          <li>
            <p class="go-body-copy">
              To open the off canvas, use the service to call
              <code class="code-block--inline">openOffCanvas</code>.
              This method takes the component type (BasicTestComponent in our case),
              the bindings for that component as an object,
              and an optional <code class="code-block--inline">offCanvasOptions</code> object with
              the header property as a string.
            </p>
            <code [highlight]="functionExample"></code>
          </li>
          <li>
            <div class="go-container">
              <div class="go-column go-column--100">
                <p class="go-body-copy go-body-copy--no-margin">
                  Lastly, we need something to trigger that function.
                  In our case we will use a button that triggers the
                  <code class="code-block--inline">openOffCanvas</code> method,
                  but it could be triggered by any event.
                </p>
              </div>
              <div class="go-column go-column--50">
                <h4 class="go-heading-4">component.html</h4>
                <code [highlight]="htmlExample"></code>
              </div>
              <div class="go-column go-column--50">
                <h4 class="go-heading-4">View</h4>
                <go-button
                  (handleClick)="openOffCanvas()"
                  buttonIcon="subdirectory_arrow_right"
                  buttonVariant="neutral">
                  Open Off Canvas
                </go-button>
              </div>
            </div>
          </li>
        </ol>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="large">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Large Off Canvas</h2>
      <go-copy cardId="large" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>
      <div class="go-column go-column--100 go-column--no-padding">
        <p class="go-body-copy">
          To render a large off canvas, add the <code class="code-block--inline">size</code> option to the 
          <code class="code-block--inline">offCanvasOptions</code> property when calling the
          <code class="code-block--inline">openOffCanvas</code> method.
        </p>
      </div>
      <div class="go-column go-column--50">
        <code [highlight]="largeOffCanvasExample"></code>
      </div>
      <div class="go-column go-column--50">
        <h4 class="go-heading-4">View</h4>
        <go-button
          (handleClick)="openLargeOffCanvas()"
          buttonIcon="subdirectory_arrow_right"
          buttonVariant="neutral">
          Open Large Off Canvas
        </go-button>
      </div>
      <div class="go-column go-column--100 go-column--no-padding">
        <h3 class="go-heading-3">Header</h3>
        <p class="go-body-copy">
          To project content from the component being rendered in the off-canvas into the header (like a submit or
          close button) wrap it in a <code class="code-block--inline">go-off-canvas-header</code> component and it will
          be rendered in the right side of the off-canvas header.
        </p>
      </div>
      <div class="go-column go-column--50">
        <code [highlight]="headerExample"></code>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="submit-button">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Off Canvas With Submit Button</h2>
      <go-copy cardId="submit-button" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>
      <div class="go-column go-column--100 go-column--no-padding">
        <p class="go-body-copy">
          To render a full-width submit button at the bottom of the off-canvas, add a
          <code class="code-block--inline">go-off-canvas-submit-button</code> element anywhere within the template
          of the component being rendered in the off-canvas.
        </p>
        <p class="go-body-copy">
          The <code class="code-block--inline">go-off-canvas-submit-button</code> component has the following bindings:
        </p>
        <code [highlight]="submitButtonInputs"></code>
        <div class="go-container">
          <div class="go-column go-column--100 go-column--no-padding">
            <h4 class="go-heading-4 go-heading--underlined">Code</h4>
            <div class="go-container">
              <div class="go-column go-column--50 go-column--no-padding">
                <h4 class="go-heading-4">component.html</h4>
                <code [highlight]="submitButtonExampleHTML"></code>
              </div>
              <div class="go-column go-column--50 go-column--no-padding">
                <h4 class="go-heading-4">component.ts</h4>
                <code [highlight]="submitButtonExampleTS"></code>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <h4 class="go-heading-4 go-heading--underlined">View</h4>
            <go-button
              (handleClick)="openSubmitButtonOffCanvas()"
              buttonIcon="subdirectory_arrow_right"
              buttonVariant="neutral">
              Open Off Canvas
            </go-button>
          </div>
        </div>
      </div>
    </div>
  </go-card>
</div>
